import { Box, Card, Divider, Typography, useTheme } from "@mui/material";
import BoatImage from "assets/images/boat.png";
import { Booking } from "lib/features/booking/types";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { bookingCardStyles } from "styles/BookingReferralDirectory/bookingCard.styles";
import { formatMoney } from "util/commonHelper";
import { parseImageUrl } from "util/parseImageUrl";

import calenderSimple from "../../../assets/icons/cali.svg";
import MessageIcon from "../../../assets/icons/envelope_mail.svg";
import MoneyIcon from "../../../assets/icons/money_icon.svg";
import PhoneCallIcon from "../../../assets/icons/phone_call.svg";
import PriceTag from "../../../assets/icons/price_tag_black.svg";
import ClockIcon from "../../../assets/icons/time.svg";
import UserAvatar from "../../../assets/icons/user-n.svg";

const baseUrl = "https://applicationimages.s3.amazonaws.com";

interface ReferralCardProps {
  referral: Booking;
}

interface CardDetails {
  image: string;
  title: string;
  price: number;
  businessPay: number;
  businessName: string;
  reservationDate: string;
  bookingEndTime: string;
  purchaseDate: string;
  commissionPay: number;
  fee: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
}

const ReferralCard = ({ referral }: ReferralCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const getImageUri = () => {
    if (!referral) return BoatImage;

    const businessUserImages = referral?.businessUser?.[0]?.businessImages?.filter((img: { record_status: boolean; }) => img.record_status === true) || [];
    if (businessUserImages.length > 0) {
      return `${baseUrl}/${businessUserImages[0].uri}`;
    }

    if (referral?.referral?.user?.avatar) {
      return `${baseUrl}/${referral?.referral?.user?.avatar}`;
    }

    const activeItemImages = referral?.referral?.item?.itemImages?.filter(img => img.record_status === true) || [];
    if (activeItemImages.length > 0) {
      return `${baseUrl}/${activeItemImages[0].uri}`;
    }

    const activeBusinessImages = referral?.referral?.business?.businessImages?.filter(img => img.record_status === true) || [];
    if (activeBusinessImages.length > 0) {
      return `${baseUrl}/${activeBusinessImages[0].uri}`;
    }

    const fallbackImage = parseImageUrl(referral?.referral?.item?.itemImages?.[0]?.uri);
    if (fallbackImage) return fallbackImage;

    return BoatImage;
  };

  let card: CardDetails = {
    image: "",
    title: "",
    price: 0,
    businessPay: 0,
    commissionPay: 0,
    businessName: "",
    reservationDate: "",
    bookingEndTime: "",
    purchaseDate: "",
    fee: 0,
    customerName: "",
    customerPhone: "",
    customerEmail: "",
  };

  const safeStringify = (value: any): string => {
    if (!value) return "";
    if (typeof value === 'string') return value;
    if (value instanceof Date) return value.toString();
    return value.toString();
  };

  card.businessPay = referral?.business_payout || 0;
  card.purchaseDate = safeStringify(referral?.created_time);
  card.commissionPay = referral?.commission_payout || 0;
  card.customerName = referral?.customer_name || "";
  card.customerPhone = referral?.customer_phone || "";
  card.customerEmail = referral?.customer_email || "";

  if (referral?.referral?.item?.fareharbor_item_id) {
    if (referral?.farehabor_booking_details) {
      card.title =
        referral?.farehabor_booking_details?.booking?.availability?.item?.name || "";
      card.businessName =
        referral?.farehabor_booking_details?.booking?.company?.name || "";
      card.reservationDate = safeStringify(
        referral?.farehabor_booking_details?.booking?.availability?.start_at
      );
      card.price =
        (referral?.farehabor_booking_details?.booking?.amount_paid || 0) / 100;
      card.bookingEndTime = safeStringify(
        referral?.farehabor_booking_details?.booking?.availability?.end_at
      );
    }
  } else {
    card.title = referral?.referral?.item?.itemDetails?.[0]?.item_name
      || referral?.referral?.business?.business_headline
      || referral?.referral?.business?.business
      || "Booking";
    card.businessName = referral?.referral?.business?.business || "";
    card.price = referral?.customer_total || 0;
    card.reservationDate = safeStringify(
      referral?.reservation_time || referral?.referral?.availability?.start_time
    );
    card.bookingEndTime = safeStringify(referral?.referral?.availability?.end_time);
  }

  const displayBusinessName = referral?.businessUser?.[0]?.business || card.businessName || "Business";

  const duration = referral?.referral?.item?.itemDetails?.[0]?.duration;

  return (
    <Card
      sx={{ ...bookingCardStyles.card(theme), padding: '0px', overflow: "hidden" }}
    >
      {referral.record_status === false && (
        <Box sx={bookingCardStyles.canceledBanner}>
          <Typography style={bookingCardStyles.canceledText}>
            Canceled
          </Typography>
        </Box>
      )}

      {/* Top section with image */}
      <Box sx={bookingCardStyles.imageBox}>
        <img
          src={getImageUri()}
          alt={"referral-image"}
          style={{ ...bookingCardStyles.image, borderRadius: 0 }}
        />
      </Box>

      {/* Details Section */}
      <Box sx={{ ...bookingCardStyles.detailsContainer, padding: "8px 24px 8px 0px" }}>

        {/* 0th Row - Title */}


        {/* Business Name and Payment Info */}
        <Box sx={bookingCardStyles.commonRow}>
          <Box >
            <Typography component="p" fontSize="17px" fontWeight="400px">
              {displayBusinessName}
            </Typography >
           
              {card?.title && (
                <Typography sx={bookingCardStyles.title}>{card?.title}</Typography>
              )}
            

          </Box>


          <Box sx={{ marginLeft: "auto" }}>
            <Box display="flex" gap="10px" alignItems="center">
            
              <Typography sx={bookingCardStyles.lightSmallText1}>
                 Purchase on {moment(card?.purchaseDate || referral?.booking_time).format("ddd, MMM D, YYYY")}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
              <Box
                sx={[
                  bookingCardStyles.iconContainer,
                ]}
              >
                <img src={PriceTag} alt="priceTag-icon" />
                <Typography style={bookingCardStyles.lightText} sx={{ display: "flex" }}>
                  <Typography sx={bookingCardStyles.darkText}>
                    Price:
                  </Typography> {formatMoney(card?.price)}
                </Typography>
              </Box>
              <Box sx={bookingCardStyles.iconContainer}>
                <img src={MoneyIcon} alt="commission-icon" />
                <Typography
                  sx={[bookingCardStyles.lightText, { color: "#328734" }]}
                >
                  <span style={{ fontWeight: "bold" }}>Commission:</span> $
                  {formatMoney(card?.commissionPay)}
                </Typography>
              </Box>
            </Box>
          </Box>

        </Box>

        <Divider sx={{ mt: "8px", mb: "0", backgroundColor: "#8D8D8D" }} />

        <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
          <Box width="100%" p="8px" borderRight={{ md: "1px solid #8D8D8D" }}>
            <Typography fontSize="17px" fontWeight="500">
              Details
            </Typography>
            <Box sx={[bookingCardStyles.commonRow, { justifyContent: "flex-start" }]}>
              <Box display="flex" gap="10px" alignItems="center">
                <img src={calenderSimple} alt="calender-icon" />
                <Typography sx={bookingCardStyles.darkText}>
                  Reservation Date:
                </Typography>
                <Typography sx={bookingCardStyles.lightSmallText}>
                  {moment(card.reservationDate || referral?.reservation_time).format("ddd, MMM D, YYYY")}
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              gap="10px"
              alignItems="center"
            >
              <img src={ClockIcon} alt="clock-icon" />
              <Typography sx={[bookingCardStyles.darkText]}>Time</Typography>
              <Typography sx={bookingCardStyles.lightSmallText}>
                {moment(card?.reservationDate).format("h:mm A")}
                {duration && ` • ${duration} hours`}
              </Typography>
            </Box>
          </Box>

          <Box width="100%" p="8px" maxWidth="450px">
            <Typography fontSize="17px" fontWeight="600">
              Customer Info
            </Typography>
            {card?.customerName && (
              <Box sx={bookingCardStyles.commonRow}>
                <Box display="flex" gap="10px" alignItems="center">
                  <img src={UserAvatar} alt="customer-icon" />
                  <Typography sx={bookingCardStyles.lightSmallText}>
                    {card?.customerName}
                  </Typography>
                </Box>
                {card?.customerPhone && (
                  <Box display="flex" gap="10px" alignItems="center" sx={{ ml: { md: "10px" } }}>
                    <img src={PhoneCallIcon} alt="phone-icon" />
                    <Typography sx={bookingCardStyles.lightSmallText}>
                      {card.customerPhone}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {card?.customerEmail && (
              <Box sx={bookingCardStyles.commonRow}>
                <Box display="flex" gap="10px" alignItems="center">
                  <img src={MessageIcon} alt="email-icon" />
                  <Typography sx={bookingCardStyles.lightSmallText}>
                    {card.customerEmail}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default ReferralCard;